<template>
  <div v-if="show" class="pixabay-overlay" @click.self="$emit('close')">
    <div class="pixabay-modal">

      <div class="pixabay-modal-header">
        <span class="pixabay-modal-title">{{ t('pixabayPickerTitle') }}</span>
        <button class="pixabay-close" @click="$emit('close')">✕</button>
      </div>

      <div class="pixabay-search-bar">
        <input
          v-model="searchQuery"
          class="pixabay-search-input"
          :placeholder="t('pixabayPickerSearchPlaceholder')"
          @keyup.enter="search"
        >
        <button class="pixabay-search-btn" @click="search">{{ t('pixabayPickerSearchButton') }}</button>
      </div>

      <div class="pixabay-body">
        <div v-if="loading" class="pixabay-status">
          {{ t('pixabayPickerLoading') }}
        </div>

        <div v-else-if="error" class="pixabay-status pixabay-error">
          <span>{{ error }}</span>
          <button v-if="!isRateLimit" class="pixabay-retry-btn" @click="search">
            {{ t('pixabayPickerRetry') }}
          </button>
        </div>

        <div v-else-if="hasSearched && images.length === 0" class="pixabay-status">
          {{ t('pixabayPickerErrorNoResults', { term: searchQuery }) }}
        </div>

        <div v-else class="pixabay-grid">
          <div
            v-for="image in images"
            :key="image.id"
            class="pixabay-image-item"
            @click="selectImage(image)"
          >
            <img :src="image.webformatURL" :alt="image.tags" class="pixabay-img">
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { translationMixin } from '@/mixins/translationMixin';

export default {
  name: 'PixabayPicker',
  mixins: [translationMixin],
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    searchTerm: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      searchQuery: '',
      images: [],
      loading: false,
      error: null,
      isRateLimit: false,
      hasSearched: false,
      lastSearchTime: 0,
    };
  },
  watch: {
    show(val) {
      if (val) {
        this.searchQuery = this.searchTerm;
        this.images = [];
        this.error = null;
        this.isRateLimit = false;
        this.hasSearched = false;
        if (this.searchQuery.trim()) {
          this.search();
        }
      }
    },
  },
  methods: {
    async search() {
      if (!this.searchQuery.trim()) return;
      const now = Date.now();
      if (now - this.lastSearchTime < 3000) {
        this.error = this.t('pixabayPickerCooldown');
        this.isRateLimit = false;
        return;
      }
      this.lastSearchTime = now;
      this.loading = true;
      this.error = null;
      this.isRateLimit = false;
      this.images = [];
      this.hasSearched = true;
      try {
        const key = process.env.VUE_APP_PIXABAY_API_KEY;
        const q = encodeURIComponent(this.searchQuery.trim().replace(/\s+/g, '+'));
        const url = `https://pixabay.com/api/?key=${key}&q=${q}&image_type=photo&orientation=horizontal&per_page=20`;
        const response = await fetch(url);
        if (response.status === 429) {
          this.isRateLimit = true;
          this.error = this.t('pixabayPickerErrorRateLimit');
          return;
        }
        if (!response.ok) {
          this.error = this.t('pixabayPickerErrorGeneral');
          return;
        }
        const data = await response.json();
        this.images = data.hits;
      } catch {
        this.error = this.t('pixabayPickerErrorGeneral');
      } finally {
        this.loading = false;
      }
    },
    selectImage(image) {
      this.$emit('image-selected', { url: image.webformatURL, pixabayId: image.id });
    },
  },
};
</script>

<style scoped>
.pixabay-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pixabay-modal {
  background: #16213e;
  border-radius: 12px;
  width: 90%;
  max-width: 480px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);
}

.pixabay-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #1e3a5f;
}

.pixabay-modal-title {
  font-size: 15px;
  font-weight: 600;
  color: #e2e8f0;
}

.pixabay-close {
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.pixabay-search-bar {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid #1e3a5f;
}

.pixabay-search-input {
  flex: 1;
  background: #0d1b2a;
  border: 1px solid #334155;
  border-radius: 6px;
  padding: 8px 10px;
  color: #e2e8f0;
  font-size: 13px;
}

.pixabay-search-input::placeholder {
  color: #64748b;
}

.pixabay-search-btn {
  background: #6366f1;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
}

.pixabay-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
}

.pixabay-status {
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
  padding: 24px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.pixabay-error {
  color: #f87171;
}

.pixabay-retry-btn {
  background: none;
  border: 1px solid #6366f1;
  color: #6366f1;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 12px;
  cursor: pointer;
}

.pixabay-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

.pixabay-image-item {
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  aspect-ratio: 16 / 9;
  transition: border-color 0.15s;
}

.pixabay-image-item:hover {
  border-color: #475569;
}

.pixabay-image-selected {
  border-color: #6366f1 !important;
}

.pixabay-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}


</style>
